import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Assuming react-router-dom is used for navigation

function OverviewList() {
  const navigate = useNavigate(); // Initialize navigate hook

  // State variables
  const [loading, setLoading] = useState(true); // Manages loading state
  const [error, setError] = useState(null); // Stores error messages
  const [allOverviewDetails, setAllOverviewDetails] = useState([]); // Stores fetched data
  const [searchTerm, setSearchTerm] = useState(''); // Stores search input
  const [sortBy, setSortBy] = useState('index'); // Stores sorting preference
  const [terminalLines, setTerminalLines] = useState([]); // Stores lines for terminal simulation
  const [currentTime, setCurrentTime] = useState(new Date()); // Stores current time

  // Simulate hacking terminal output
  const hackingCommands = [
    'GLOCYBS > Initializing neural network breach...',
    'GLOCYBS > Scanning for vulnerabilities...',
    'GLOCYBS > Found 47 open ports',
    'GLOCYBS > Bypassing firewall protocols...',
    'GLOCYBS > Decrypting secure channels...',
    'GLOCYBS > Access granted to intelligence database',
    'GLOCYBS > Extracting classified information...',
    'GLOCYBS > Data retrieval complete'
  ];

  // useEffect hook for initial loading simulation and time update
  useEffect(() => {
    // Simulate terminal loading
    const timer = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < hackingCommands.length) {
          setTerminalLines(prev => [...prev, hackingCommands[index]]);
          index++;
        } else {
          clearInterval(interval);
          // After terminal simulation, fetch data
          setTimeout(() => {
            fetchAllOverviewDetails();
          }, 1000);
        }
      }, 500); // Add a new line every 500ms
    }, 1000); // Start simulation after 1 second

    // Update current time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Cleanup function to clear timers and intervals on component unmount
    return () => {
      clearTimeout(timer);
      clearInterval(timeInterval);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  // Function to fetch all overview details from the API
  const fetchAllOverviewDetails = async () => {
    setLoading(true); // Set loading to true before fetching
    setError(null); // Clear any previous errors
    try {
      // Determine API URL, defaulting to localhost if REACT_APP_API_URL is not set
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${apiUrl}/overview`); // Fetch data from the overview endpoint
      if (!response.ok) {
        // If response is not OK (e.g., 404, 500), throw an error
        throw new Error('Failed to fetch overview details');
      }
      const data = await response.json(); // Parse the JSON response

      // Check if data.data exists and is an array, otherwise use data directly
      if (data.data && Array.isArray(data.data)) {
        setAllOverviewDetails(data.data);
      } else {
        setAllOverviewDetails(data);
      }
    } catch (error) {
      setError(error.message); // Catch and set any errors during the fetch
    } finally {
      setLoading(false); // Set loading to false after fetch completes (success or error)
    }
  };

  // Handler for the "Go Back" button
  const handleGoBack = () => {
    navigate('/'); // Navigate to the home page
  };

  // Filter data based on search term
  const filteredData = allOverviewDetails.filter(item =>
    item.heading?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort filtered data based on sortBy preference
  const sortedData = [...filteredData].sort((a, b) => {
    if (sortBy === 'heading') {
      return a.heading?.localeCompare(b.heading) || 0; // Sort alphabetically by heading
    }
    return 0; // Default: no specific sort if sortBy is not 'heading'
  });

  // Conditional rendering for loading state
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.terminalWindow}>
          <div style={styles.terminalHeader}>
            <div style={styles.terminalButtons}>
              <div style={styles.terminalButton}></div>
              <div style={styles.terminalButton}></div>
              <div style={styles.terminalButton}></div>
            </div>
            <div style={styles.terminalTitle}>GLOCYBS Security Terminal v2.1.3</div>
          </div>
          <div style={styles.terminalBody}>
            <div style={styles.terminalPrompt}>
              <span style={styles.promptText}>root@glocybs:~$ </span>
              <span style={styles.blinkingCursor}>█</span>
            </div>
            {terminalLines.map((line, index) => (
              <div key={index} style={styles.terminalLine}>
                {line}
              </div>
            ))}
            <div style={styles.loadingSpinner}>
              <div style={styles.spinnerDot}></div>
              <div style={styles.spinnerDot}></div>
              <div style={styles.spinnerDot}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Conditional rendering for error state
  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorTerminal}>
          <div style={styles.errorHeader}>
            <div style={styles.errorIcon}>⚠️</div>
            <h2 style={styles.errorTitle}>SECURITY BREACH DETECTED</h2>
          </div>
          <div style={styles.errorContent}>
            <p style={styles.errorMessage}>
              GLOCYBS > Connection to secure server terminated
            </p>
            <div style={styles.errorDetails}>ERROR_CODE: {error}</div>
            <div style={styles.errorWarning}>
              ⚠️ Unauthorized access attempt logged
            </div>
          </div>
          <button onClick={handleGoBack} style={styles.primaryButton}>
            RECONNECT TO MAINFRAME
          </button>
        </div>
      </div>
    );
  }

  // Conditional rendering for no data state (after loading and no error)
  if (!allOverviewDetails || allOverviewDetails.length === 0) {
    return (
      <div style={styles.noDataContainer}>
        <div style={styles.noDataTerminal}>
          <div style={styles.noDataIcon}>🔒</div>
          <h2 style={styles.noDataTitle}>DATABASE ENCRYPTED</h2>
          <p style={styles.noDataText}>
            GLOCYBS intelligence database is currently locked or inaccessible.
          </p>
          <button onClick={handleGoBack} style={styles.primaryButton}>
            RETURN TO COMMAND CENTER
          </button>
        </div>
      </div>
    );
  }

  // Main component rendering for displaying data
  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.glocybsLogo}>
            <span style={styles.logoText}>GLOCYBS</span>
            <span style={styles.logoSubtext}>Global Cybersecurity Intelligence</span>
          </div>
          <div style={styles.statusBar}>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>STATUS:</span>
              <span style={styles.statusValue}>ACTIVE</span>
            </div>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>SECURITY:</span>
              <span style={styles.statusValue}>BREACH MODE</span>
            </div>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>TIME:</span>
              <span style={styles.statusValue}>{currentTime.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Control Panel Section (Search, Sort, Stats) */}
      <div style={styles.controlPanel}>
        <div style={styles.hackingInterface}>
          <div style={styles.searchContainer}>
            <span style={styles.prompt}>root@glocybs:~$ search </span>
            <input
              type="text"
              placeholder="Enter target query..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          
          <div style={styles.sortContainer}>
            <span style={styles.sortLabel}>SORT_BY:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.sortSelect}
            >
              <option value="index">CHRONOLOGICAL</option>
              <option value="heading">ALPHABETICAL</option>
            </select>
          </div>

          <div style={styles.statsContainer}>
            <div style={styles.statItem}>
              <span style={styles.statNumber}>{allOverviewDetails.length}</span>
              <span style={styles.statLabel}>TARGETS</span>
            </div>
            <div style={styles.statItem}>
              <span style={styles.statNumber}>{filteredData.length}</span>
              <span style={styles.statLabel}>MATCHED</span>
            </div>
            <div style={styles.statItem}>
              <span style={styles.statNumber}>100%</span>
              <span style={styles.statLabel}>COMPROMISED</span>
            </div>
          </div>
        </div>
      </div>

      {/* List Container (Grid of Cards) */}
      <div style={styles.listContainer}>
        <div style={styles.gridContainer}>
          {sortedData.map((item, index) => (
            <div key={index} style={styles.card} className="card-hover">
              <div style={styles.cardHeader}>
                <div style={styles.cardNumber}>
                  TARGET_{String(allOverviewDetails.indexOf(item) + 1).padStart(3, '0')}
                </div>
                <div style={styles.cardBadge}>
                  <span style={styles.blinkingDot}>●</span>
                  COMPROMISED
                </div>
              </div>
              
              <div style={styles.cardContent}>
                <div style={styles.cardTitle}>
                  <span style={styles.hackingPrefix}>GLOCYBS > </span>
                  {item.heading}
                </div>
                <div style={styles.cardDescription}>
                  <div style={styles.dataStream}>
                    <span style={styles.dataPrefix}>DATA_EXTRACT:</span>
                    <div style={styles.dataContent}>{item.content}</div>
                  </div>
                </div>
              </div>
              
              <div style={styles.cardFooter}>
                <div style={styles.cardActions}>
                  <span style={styles.actionTag}>
                    {item.button_text || 'EXPLOIT_FOUND'}
                  </span>
                </div>
                <div style={styles.cardMeta}>
                  <span style={styles.metaText}>
                    ID: {allOverviewDetails.indexOf(item) + 1}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Section */}
      <div style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerLeft}>
            <div style={styles.connectionStatus}>
              <span style={styles.connectionDot}>●</span>
              Connected to GLOCYBS Mainframe
            </div>
          </div>
          <button onClick={handleGoBack} style={styles.backButton}>
            <span style={styles.buttonIcon}>⬅</span>
            EXIT SYSTEM
          </button>
        </div>
      </div>

      {/* Global Styles with Keyframe Animations */}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px #00ff00; }
          50% { box-shadow: 0 0 20px #00ff00; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spinnerPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        
        .card-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0, 255, 0, 0.2);
        }
        
        .terminal-glow {
          animation: glow 2s ease-in-out infinite alternate;
        }
      `}</style>
    </div>
  );
}

// Inline styles object for the component
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    color: '#00ff00',
    position: 'relative',
    overflow: 'hidden',
  },
  
  header: {
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
    color: '#00ff00',
    padding: '40px 0',
    borderBottom: '2px solid #00ff00',
    position: 'relative',
  },
  
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
  },
  
  glocybsLogo: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  
  logoText: {
    fontSize: '48px',
    fontWeight: '700',
    color: '#00ff00',
    textShadow: '0 0 20px #00ff00',
    display: 'block',
    letterSpacing: '8px',
  },
  
  logoSubtext: {
    fontSize: '16px',
    color: '#00aa00',
    textTransform: 'uppercase',
    letterSpacing: '3px',
    marginTop: '10px',
    display: 'block',
  },
  
  statusBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    flexWrap: 'wrap',
  },
  
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 16px',
    border: '1px solid #00ff00',
    borderRadius: '4px',
    background: 'rgba(0, 255, 0, 0.1)',
  },
  
  statusLabel: {
    fontSize: '12px',
    color: '#00aa00',
  },
  
  statusValue: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#00ff00',
  },
  
  controlPanel: {
    background: 'rgba(0, 0, 0, 0.8)',
    padding: '30px',
    borderBottom: '1px solid #00ff00',
    backdropFilter: 'blur(10px)',
  },
  
  hackingInterface: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.9)',
    border: '1px solid #00ff00',
    borderRadius: '4px',
    padding: '10px 15px',
    flex: '1',
    maxWidth: '500px',
  },
  
  prompt: {
    color: '#00ff00',
    fontSize: '14px',
    fontWeight: 'bold',
    marginRight: '10px',
  },
  
  searchInput: {
    background: 'transparent',
    border: 'none',
    color: '#00ff00',
    fontSize: '14px',
    outline: 'none',
    flex: '1',
    fontFamily: 'inherit',
  },
  
  sortContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'rgba(0, 0, 0, 0.9)',
    border: '1px solid #00ff00',
    borderRadius: '4px',
    padding: '10px 15px',
  },
  
  sortLabel: {
    fontSize: '12px',
    color: '#00aa00',
  },
  
  sortSelect: {
    background: 'transparent',
    border: 'none',
    color: '#00ff00',
    fontSize: '12px',
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  
  statsContainer: {
    display: 'flex',
    gap: '20px',
  },
  
  statItem: {
    textAlign: 'center',
    padding: '10px 20px',
    background: 'rgba(0, 255, 0, 0.1)',
    border: '1px solid #00ff00',
    borderRadius: '4px',
    minWidth: '80px',
  },
  
  statNumber: {
    display: 'block',
    fontSize: '20px',
    fontWeight: '700',
    color: '#00ff00',
    marginBottom: '4px',
  },
  
  statLabel: {
    fontSize: '10px',
    color: '#00aa00',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  
  listContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '30px',
  },
  
  card: {
    background: 'rgba(0, 0, 0, 0.9)',
    border: '1px solid #00ff00',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    animation: 'fadeIn 0.6s ease-out',
    className: 'card-hover', // This className will be applied to the div
    position: 'relative',
  },
  
  cardHeader: {
    padding: '20px',
    background: 'rgba(0, 255, 0, 0.1)',
    borderBottom: '1px solid #00ff00',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  cardNumber: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#00ff00',
    fontFamily: 'inherit',
    textShadow: '0 0 10px #00ff00',
  },
  
  cardBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255, 0, 0, 0.2)',
    color: '#ff0000',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    border: '1px solid #ff0000',
  },
  
  blinkingDot: {
    animation: 'blink 1s infinite',
  },
  
  cardContent: {
    padding: '20px',
  },
  
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#00ff00',
    marginBottom: '15px',
    lineHeight: '1.4',
  },
  
  hackingPrefix: {
    color: '#00aa00',
    fontSize: '14px',
  },
  
  cardDescription: {
    marginBottom: '0',
  },
  
  dataStream: {
    background: 'rgba(0, 0, 0, 0.5)',
    padding: '15px',
    borderRadius: '4px',
    border: '1px solid #00aa00',
  },
  
  dataPrefix: {
    fontSize: '12px',
    color: '#00aa00',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '8px',
  },
  
  dataContent: {
    fontSize: '14px',
    color: '#00ff00',
    lineHeight: '1.5',
  },
  
  cardFooter: {
    padding: '20px',
    background: 'rgba(0, 255, 0, 0.05)',
    borderTop: '1px solid #00ff00',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  cardActions: {
    display: 'flex',
    gap: '12px',
  },
  
  actionTag: {
    background: 'rgba(255, 165, 0, 0.2)',
    color: '#ffaa00',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    border: '1px solid #ffaa00',
  },
  
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  metaText: {
    fontSize: '12px',
    color: '#00aa00',
    fontWeight: '500',
  },
  
  footer: {
    padding: '30px 20px',
    background: 'rgba(0, 0, 0, 0.9)',
    borderTop: '1px solid #00ff00',
  },
  
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  footerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  
  connectionStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#00ff00',
    fontSize: '14px',
  },
  
  connectionDot: {
    color: '#00ff00',
    animation: 'blink 1s infinite',
  },
  
  backButton: {
    background: 'rgba(0, 0, 0, 0.9)',
    color: '#00ff00',
    border: '2px solid #00ff00',
    borderRadius: '4px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: 'inherit',
  },
  
  buttonIcon: {
    fontSize: '16px',
  },
  
  primaryButton: {
    background: 'rgba(0, 0, 0, 0.9)',
    color: '#00ff00',
    border: '2px solid #00ff00',
    borderRadius: '4px',
    padding: '16px 32px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: 'inherit',
  },
  
  // Loading states
  loadingContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    color: '#00ff00',
  },
  
  terminalWindow: {
    width: '800px',
    background: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid #00ff00',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 0 30px rgba(0, 255, 0, 0.3)',
    className: 'terminal-glow', // This className will be applied to the div
  },
  
  terminalHeader: {
    background: 'rgba(0, 255, 0, 0.1)',
    padding: '10px 15px',
    borderBottom: '1px solid #00ff00',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  
  terminalButtons: {
    display: 'flex',
    gap: '8px',
  },
  
  terminalButton: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#00ff00',
    opacity: 0.6,
  },
  
  terminalTitle: {
    fontSize: '14px',
    color: '#00ff00',
    fontWeight: '600',
  },
  
  terminalBody: {
    padding: '20px',
    minHeight: '400px',
    background: 'rgba(0, 0, 0, 0.8)',
  },
  
  terminalPrompt: {
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
  },
  
  promptText: {
    color: '#00ff00',
    fontSize: '14px',
  },
  
  blinkingCursor: {
    animation: 'blink 1s infinite',
    fontSize: '14px',
    color: '#00ff00',
  },
  
  terminalLine: {
    color: '#00ff00',
    fontSize: '14px',
    marginBottom: '10px',
    lineHeight: '1.4',
  },
  
  loadingSpinner: {
    display: 'flex',
    gap: '8px',
    marginTop: '20px',
  },
  
  spinnerDot: {
    width: '8px',
    height: '8px',
    background: '#00ff00',
    borderRadius: '50%',
    animation: 'spinnerPulse 1.5s ease-in-out infinite',
  },
  
  // Error states
  errorContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    color: '#ff0000',
  },
  
  errorTerminal: {
    background: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid #ff0000',
    borderRadius: '8px',
    padding: '40px',
    textAlign: 'center',
    boxShadow: '0 0 30px rgba(255, 0, 0, 0.3)',
    minWidth: '500px',
  },
  
  errorHeader: {
    marginBottom: '30px',
  },
  
  errorIcon: {
    fontSize: '60px',
    marginBottom: '20px',
  },
  
  errorTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ff0000',
    marginBottom: '20px',
    textShadow: '0 0 10px #ff0000',
  },
  
  errorContent: {
    marginBottom: '30px',
  },
  
  errorMessage: {
    fontSize: '16px',
    color: '#ff0000',
    marginBottom: '15px',
    lineHeight: '1.6',
  },
  
  errorDetails: {
    fontSize: '12px',
    color: '#ff6666',
    marginBottom: '15px',
    background: 'rgba(255, 0, 0, 0.1)',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ff0000',
  },
  
  errorWarning: {
    fontSize: '14px',
    color: '#ffaa00',
    fontWeight: '600',
  },
  
  // No data states
  noDataContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    color: '#ffaa00',
  },
  
  noDataTerminal: {
    background: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid #ffaa00',
    borderRadius: '8px',
    padding: '40px',
    textAlign: 'center',
    boxShadow: '0 0 30px rgba(255, 170, 0, 0.3)',
    minWidth: '500px',
  },
  
  noDataIcon: {
    fontSize: '60px',
    marginBottom: '20px',
  },
  
  noDataTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffaa00',
    marginBottom: '20px',
    textShadow: '0 0 10px #ffaa00',
  },
  
  noDataText: {
    fontSize: '16px',
    color: '#ffaa00',
    marginBottom: '30px',
    lineHeight: '1.6',
  },
};

export default OverviewList;
